/**
 * Servicio para gestionar Products
 */
import { apiService } from './api';
import { Product, CreateProductDto, UpdateProductDto, ProductListResponse } from '../types/product.type';
import { ProductCategory } from '../constants/productCategory';

export class ProductsService {
  private baseEndpoint = '/products';

  /**
   * Obtiene todos los productos
   */
  async getAll(params?: {
    page?: number;
    limit?: number;
    category?: string;
    isActive?: boolean;
    search?: string;
  }): Promise<ProductListResponse> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.category) queryParams.append('category', params.category);
    if (params?.isActive !== undefined) queryParams.append('isActive', params.isActive.toString());
    if (params?.search) queryParams.append('search', params.search);

    const queryString = queryParams.toString();
    const endpoint = queryString ? `${this.baseEndpoint}?${queryString}` : this.baseEndpoint;
    
    return apiService.get<ProductListResponse>(endpoint);
  }

  /**
   * Obtiene un producto por ID
   */
  async getById(id: string): Promise<Product> {
    return apiService.get<Product>(`${this.baseEndpoint}/${id}`);
  }

  /**
   * Obtiene un producto por SKU
   */
  async getBySku(sku: string): Promise<Product> {
    return apiService.get<Product>(`${this.baseEndpoint}/sku/${sku}`);
  }

  /**
   * Crea un nuevo producto
   */
  async create(data: CreateProductDto): Promise<Product> {
    return apiService.post<Product>(this.baseEndpoint, data);
  }

  /**
   * Actualiza un producto
   */
  async update(id: string, data: UpdateProductDto): Promise<Product> {
    return apiService.put<Product>(`${this.baseEndpoint}/${id}`, data);
  }

  /**
   * Actualiza parcialmente un producto
   */
  async patch(id: string, data: UpdateProductDto): Promise<Product> {
    return apiService.patch<Product>(`${this.baseEndpoint}/${id}`, data);
  }

  /**
   * Elimina un producto
   */
  async delete(id: string): Promise<void> {
    return apiService.delete<void>(`${this.baseEndpoint}/${id}`);
  }

  /**
   * Obtiene productos por categoría
   */
  async getByCategory(category: string, params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
  }): Promise<ProductListResponse> {
    return this.getAll({ ...params, category });
  }

  /**
   * Obtiene productos por categoría usando el enum ProductCategory
   */
  async getProductsByCategory(category: ProductCategory, params?: {
    page?: number;
    limit?: number;
    isActive?: boolean;
  }): Promise<ProductListResponse> {
    return this.getAll({ ...params, category });
  }
}

// Instancia singleton del servicio
export const productsService = new ProductsService();

